import React, {useEffect, useState, FunctionComponent} from "react";
import {makeStyles, createStyles, useTheme, Theme} from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import ChatContainer from "../ChatContainer";
import {
    useMediaQuery,
    Typography,
    Radio,
    RadioGroup,
    FormControl,
    FormControlLabel,
    Button,
    Box, CircularProgress, TextField
} from "@material-ui/core";
import {RootState} from "../../redux/store";
import {useDispatch, useSelector} from "react-redux";

import {PetEdit, NoteEdit, PersonEdit, PetMedicalConditions} from "../Forms/AppointmentForms";
import {setSelectedAnimal} from "../../redux/pets/pets.actions";
import {getAppointmentNotes} from "../../redux/appointment/appointment.actions";
import moment from "moment";
import {getAppointmentType} from "../../Constants/serviceType";
import NotesListItem from "../ListItems/NotesListItem";
import {colorInputBorder, colorInputBorderGrayish} from "../../Constants/theme";
import useAgora from "../../hooks/useAgora";
import AgoraRTC from "agora-rtc-sdk-ng";

interface Props {
    data: any,
    over?: boolean,
    close?: boolean,
    handleChatData?: (open, data, over) => void,
    handleCloseTranscript?: () => void,
    isJoin?: string,
    videoChatIsClosed?: null | string,
    setVideoChatIsClosed?: (string) => void,
}

const useStyle = makeStyles((theme: Theme) => createStyles({
    topSpacing: {
        paddingTop: "40px",
    },
    root: {
        display: "flex",
        width: "90%",
        marginTop: 20,
        alignSelf: "center",
    },
    details: {
        display: "flex",
        flexDirection: "column",
        marginLeft: 10,
        width: "100%"
    },
    content: {
        flex: "auto",
        width: "100%"
    },
    appointmentInfo: {
        width: "100%",
        paddingBottom: 10
    },
    radioForm: {
        paddingTop: 20,
        display: "flex",
        width: "90%",
        margin: "20px auto",
        alignSelf: "center",
        justifySelf: "center"
    },
    button: {
        justifyContent: "center",

    },
    itemDivider: {
        borderBottom: `1px solid ${colorInputBorderGrayish}`
    },
    textPlain: {
        fontSize: 18,
        whiteSpace: "nowrap",
    },
    formContainer: {
        border: "1px solid #DDDDDD",
        borderRadius: 5,
        padding: 17,
        marginTop: 22,
    },
    textSecondary: {
        fontSize: 18,
        marginBottom: 10,
    },
    listNotes: {
        maxHeight: 500,
        overflowY: "scroll",
        "&::-webkit-scrollbar": {
            width: 5,
        },
        "&::-webkit-scrollbar-thumb": {
            background: colorInputBorder,
            borderRadius: "6px",
        },
    }
}));

//Create the various validation schemas for each form:
//SOAP/NOTES
const client = AgoraRTC.createClient({codec: "vp8", mode: "rtc"});

const VetAppointmentView: FunctionComponent<Props> = (
    {
        data,
        over,
        close,
        handleChatData,
        handleCloseTranscript,
        isJoin,
        videoChatIsClosed,
        setVideoChatIsClosed,
    }: Props) => {
    const dispatch = useDispatch();
    const classes = useStyle();
    const [isFinished, setIsFinished] = useState(null);
    const [recordsList, setRecordsList] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [title, setTitle] = useState("");
    const [date, setDate] = useState("");
    const [isView, setIsView] = useState(false);
    const [isNotesExpand, setNotesExpand] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
    const appointmentData = useSelector((state: RootState) => state?.appointment.appointment);
    const [hasConfirmedInfo, setHasConfirmedInfo] = useState("false");
    //appointment status function
    const requestAppointment = async () => {
        // eslint-disable-next-line no-prototype-builtins
        appointmentData.hasOwnProperty("finished") ? setIsFinished(appointmentData.finished) : setIsFinished(false);
    };
    const agoraService = useAgora(client);
    const {
        leave,
    } = agoraService;

    useEffect(() => {
        requestAppointment();
        dispatch(setSelectedAnimal(data.data.item.animal));

    }, []);

    const handleInfoConfChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setHasConfirmedInfo((event.target as HTMLInputElement).value);
    };
    const handleNotes = async () => {
        try {
            const records = await dispatch(getAppointmentNotes(
                {animalId: data.data.item.animal.id}
            ));
            setRecordsList(records);
            setNotesExpand(!isNotesExpand);
        } finally {
            setIsLoading(false);
        }
    };
    const handleNotesDetails = (id, selectedTitle, selectedDate) => {
        setSelectedRecord(recordsList.find(el=> el.id === id));
        setTitle(selectedTitle);
        setDate(selectedDate);
    };

    return (
        <Grid container spacing={0} justify="center">
            <Grid item xs={12} sm={12} md={4} lg={4} xl={4} className={classes.topSpacing}>
                <PetEdit data={data} isFinished={isFinished} setIsFinished={setIsFinished} isMobile={isMobile}/>
                <div style={{alignItems: "center", paddingRight: 19, paddingLeft: 19}}>
                    <Button
                        type="button"
                        color="secondary"
                        variant="contained"
                        className={classes.button}
                        onClick={handleNotes}
                    >
                        {!isNotesExpand ? "Expand Notes/History" : "Compress Notes/History"}
                    </Button>
                    {isNotesExpand && (
                        !isView ? (
                            <div className={classes.listNotes}>{
                                (recordsList?.length && !isLoading) ? (
                                    [...recordsList]
                                        .sort((a, b) => moment(b.created).diff(moment(a.created)))
                                        .map(({id, created, appointment, primaryVet}, idx) => {
                                            const vet = data.data.vetProfile;
                                            const title = appointment.status === "NOT_APPLICABLE" ? "In-Clinic Appointment" : `${getAppointmentType(appointment?.type)} ${vet?.lastName? (`- Dr. ${vet.lastName}`) : ""}`;
                                            const noteCreated = moment(created).format("MMMM DD, YYYY");
                                            return <NotesListItem
                                                key={idx}
                                                id={id}
                                                title={title}
                                                date={noteCreated}
                                                setSelectedRecord={handleNotesDetails}
                                                setOpenModal={setIsView}
                                                styles={(idx === recordsList.length - 1) ? "" : classes.itemDivider}
                                            />;
                                        })) :
                                    <Typography variant="h6">No medical records</Typography>}</div>) : (
                            <div className={classes.formContainer}>
                                <Grid item xs={12} sm={9}>
                                    <Box>
                                        <Typography variant="h4">{title}</Typography>
                                        <Typography color="textSecondary" className={classes.textSecondary}>{date}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box marginBottom="15px">
                                        <Typography variant="h4" className={classes.textPlain}>
                                            Subjective
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box marginBottom="20px">
                                        <TextField
                                            multiline
                                            fullWidth
                                            value={selectedRecord?.subjective}
                                            variant="outlined"
                                            disabled
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box marginBottom="15px">
                                        <Typography variant="h4" className={classes.textPlain}>
                                            Objective
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box marginBottom="20px">
                                        <TextField
                                            multiline
                                            fullWidth
                                            value={selectedRecord?.objective}
                                            variant="outlined"
                                            disabled
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box marginBottom="15px">
                                        <Typography variant="h4" className={classes.textPlain}>
                                            Assessment
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box marginBottom="20px">
                                        <TextField
                                            multiline
                                            fullWidth
                                            value={selectedRecord?.assessment}
                                            variant="outlined"
                                            disabled
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box marginBottom="15px">
                                        <Typography variant="h4" className={classes.textPlain}>
                                            Plan
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box marginBottom="20px">
                                        <TextField
                                            multiline
                                            fullWidth
                                            value={selectedRecord?.plan}
                                            variant="outlined"
                                            disabled
                                        />
                                    </Box>
                                </Grid>
                                <Button variant="contained" component="label"
                                    style={{height: 34}}
                                    onClick={() => {
                                        setIsView(false);
                                    }}>
                                    Close
                                </Button>
                            </div>)
                    )}
                </div>
                <PersonEdit data={data} isFinished={isFinished} setIsFinished={setIsFinished} isMobile={isMobile}/>
                <div className={classes.radioForm}>
                    <Grid container justify="center" alignItems="center">
                        <Grid item xs={12}>
                            <Typography variant="h4">Step 1: </Typography>
                            <Typography>Client and Patient information has been verified: </Typography>
                            <FormControl component="fieldset">
                                <RadioGroup row aria-label="confirmedInfo" name="confirmedInfo"
                                    value={hasConfirmedInfo}
                                    onChange={handleInfoConfChange}>
                                    <FormControlLabel value="true" control={<Radio/>} label="Yes"/>
                                    <FormControlLabel value="false" control={<Radio/>} label="No"/>
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                    </Grid>
                </div>
                <PetMedicalConditions/>
            </Grid>
            <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                <ChatContainer
                    data={data}
                    over={over}
                    close={close}
                    handleChatData={handleChatData}
                    handleCloseTranscript={handleCloseTranscript}
                    isVet={true}
                    isJoin={isJoin}
                    videoChatIsClosed={videoChatIsClosed}
                    setVideoChatIsClosed={setVideoChatIsClosed}
                />
            </Grid>
            <Grid item xs={12} sm={12} md={4} lg={4} xl={4} className={classes.topSpacing}
                style={{paddingLeft: 25}}>
                <NoteEdit data={data} isFinished={isFinished} setIsFinished={setIsFinished} isMobile={isMobile}
                    hasConfirmedInfo={hasConfirmedInfo} leave={leave}
                    videoChatIsClosed={videoChatIsClosed}
                    setVideoChatIsClosed={setVideoChatIsClosed}
                />
            </Grid>
        </Grid>
    );
};

export default VetAppointmentView;
